import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { usePopper } from 'react-popper';
import styled, { css, CSSObject } from 'styled-components';
import useOnClickOutside from 'use-onclickoutside';
import { ReactEditor, useSlate } from 'slate-react';
import { Colors } from 'styles';
import { PicklistElement } from './PicklistElement';
import { useRegisterNodeSelectedStateType } from '../../../hooks';
import { areMultipleSiblingBracketsSelected, getExpandedPicklistSelection } from '../../../utils';
import { getActivePicklist } from '../../../utils/selection';
import { Portal } from 'common/ui/Portal';
import { transparentize } from 'color2k';
import { getPicklistOptionDisplayName } from '../utils';
import { usePicklistEditor } from '../../../../Template/usePicklist';
import { useActivePicklistState } from '../../../../Template/useActivePicklistState';
import type { PicklistPluginElement, ReportPicklistBeforeSubmissionOptions } from '../types';
import { PicklistOption } from './PicklistOption';
import type { AllPluginID } from '../../types';
import type { RenderElementProps } from '../../../core';
import { useSelected, Range } from '../../../core';
import { rootTheme } from 'theme/rootTheme';
import { isSelectionContainedInNode } from '../../../utils/isSelectionContainedInNode';
import { useSendModifyDictationMessage } from 'common/Recorder/useRecorder';

const Cell = styled.span`
  overflow: hidden;
`;

type PicklistSelectorProps = {
  options: ReadonlyArray<ReportPicklistBeforeSubmissionOptions>;
  children: Array<ReactNode> | ReactNode;
  attributes: RenderElementProps['attributes'];
  isSelected: boolean;
  pluginID: AllPluginID;
  showPicklistOptionsInEditor: boolean;
  element: PicklistPluginElement;
  ['data-editor-element']?: string;
  leftDelimiter?: string;
  rightDelimiter?: string;
};

export const PicklistSelector = ({
  options,
  children,
  pluginID,
  attributes,
  showPicklistOptionsInEditor,
  element,
  ...props
}: PicklistSelectorProps): ReactElement => {
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);

  const { selectedPicklistOptionIds, setSelectedPicklistOptionIds, selectPicklistItem } =
    usePicklistEditor();
  const sendModifyDictationMessage = useSendModifyDictationMessage();
  const { isActivePicklistMatch } = useActivePicklistState(element.picklistID);
  const [hoveredPicklistOptionIndex, setHoveredPicklistOptionIndex] = useState<number | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLUListElement | null>(null);

  const handlePopperRef = useCallback((node: HTMLUListElement | null) => {
    setPopperElement((prev) => (prev === node ? prev : node));
  }, []);

  const { styles, update } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-start',
    modifiers: [{ name: 'flip', options: { fallbackPlacements: ['top-start', 'bottom-start'] } }],
  });
  const editor = useSlate();
  const selected = useSelected();
  const moveItemUp = useCallback(() => {
    setSelectedPicklistOptionIds((prev) => {
      const selectedId = prev[element.picklistID];
      const selectedIndex = options.findIndex((o) => o.id === selectedId);
      if (selectedIndex === -1 || options.length === 0) {
        return prev;
      }
      const nextIndex = selectedIndex > 0 ? selectedIndex - 1 : options.length - 1;
      const nextOption = options[nextIndex];
      if (!nextOption) return prev;
      return { ...prev, [element.picklistID]: nextOption.id };
    });
  }, [setSelectedPicklistOptionIds, options, element.picklistID]);

  const moveItemDown = useCallback(() => {
    setSelectedPicklistOptionIds((prev) => {
      const selectedId = prev[element.picklistID];
      const selectedIndex = options.findIndex((o) => o.id === selectedId);
      if (selectedIndex === -1 || options.length === 0) {
        return prev;
      }
      const nextIndex = selectedIndex < options.length - 1 ? selectedIndex + 1 : 0;
      const nextOption = options[nextIndex];
      if (!nextOption) return prev;
      return { ...prev, [element.picklistID]: nextOption.id };
    });
  }, [setSelectedPicklistOptionIds, options, element.picklistID]);

  const resetActiveSelectedPicklistOption = useCallback(() => {
    setSelectedPicklistOptionIds((prev) => {
      if (options.length === 0) {
        return prev;
      }
      const nextOption = options[0];
      if (!nextOption) return prev;
      return {
        ...prev,
        [element.picklistID]: nextOption.id,
      };
    });
  }, [element.picklistID, options, setSelectedPicklistOptionIds]);

  const multipleSiblingBracketsSelected = areMultipleSiblingBracketsSelected(editor);
  // Picklist should only be open if the picklist is entirely selected or not expanded at all
  const shouldPicklistBeOpen = useMemo(() => {
    const expandedPicklistSelection = getExpandedPicklistSelection(editor);
    let isSelectionWithinCurrentPicklist = false;
    let isActivePicklistElement = false;
    let isImmediateSelectionPicklistMatch = false;

    try {
      if (editor.selection != null) {
        const currentElementPath = ReactEditor.findPath(editor, element);
        const activePicklistEntry = getActivePicklist(editor, editor.selection);
        // Guard against opening multiple selectors in nested content:
        // only the picklist that matches the active picklist path should be open.
        isActivePicklistElement =
          activePicklistEntry != null &&
          JSON.stringify(activePicklistEntry[1]) === JSON.stringify(currentElementPath);
        isImmediateSelectionPicklistMatch =
          activePicklistEntry != null &&
          'picklistID' in activePicklistEntry[0] &&
          activePicklistEntry[0].picklistID === element.picklistID;
        // For expanded selections, ensure the current selection still belongs to this picklist.
        // This keeps split/nested structures from rendering stale dropdowns on sibling picklists.
        isSelectionWithinCurrentPicklist = isSelectionContainedInNode(
          editor,
          currentElementPath,
          editor.selection
        );
      }
    } catch (error) {
      // Path lookups can throw during transient selection/path desync while Slate is updating.
      // Failing closed here avoids a crash and prevents accidental dropdown open state.
      isSelectionWithinCurrentPicklist = false;
    }

    return (
      editor.selection != null &&
      isActivePicklistElement &&
      // `activePicklist` is effect-driven and can lag selection by one render while tabbing.
      // Allow the immediate Slate selection match to keep the dropdown stable in that frame.
      (isActivePicklistMatch || isImmediateSelectionPicklistMatch) &&
      !multipleSiblingBracketsSelected &&
      (Range.isCollapsed(editor.selection) ||
        (expandedPicklistSelection != null &&
          Range.equals(expandedPicklistSelection, editor.selection)) ||
        (expandedPicklistSelection == null && isSelectionWithinCurrentPicklist))
    );
  }, [editor, isActivePicklistMatch, multipleSiblingBracketsSelected, element]);
  const [{ isOpen }, setState] = useRegisterNodeSelectedStateType(
    {
      isOpen: shouldPicklistBeOpen,
      moveItemUp,
      moveItemDown,
      selectPicklistItem,
      resetActiveSelectedPicklistOption,
    },
    pluginID
  );

  // Keep local open state aligned with the current picklist selection to avoid stale dropdowns.
  useEffect(() => {
    setState((state) =>
      state.isOpen === shouldPicklistBeOpen ? state : { ...state, isOpen: shouldPicklistBeOpen }
    );
  }, [setState, shouldPicklistBeOpen]);

  useEffect(() => {
    setState((s) => ({ ...s, moveItemUp, moveItemDown, selectPicklistItem }));
  }, [setState, moveItemUp, moveItemDown, selectPicklistItem]);

  const handleSelectPicklistItem = useCallback(
    (index: number) => {
      selectPicklistItem(index);
      sendModifyDictationMessage();
    },
    [selectPicklistItem, sendModifyDictationMessage]
  );

  useEffect(() => {
    if (!isOpen) {
      setHoveredPicklistOptionIndex(null);
    }
  }, [isOpen]);

  const popperRef = useRef<HTMLUListElement | null>(popperElement);

  useEffect(() => {
    popperRef.current = popperElement;
  }, [popperElement]);

  const handleOutsideClick = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const target = event.target;
      if (target instanceof Node && !referenceElement?.contains(target)) {
        setState((state) => ({ ...state, isOpen: false }));
      }
    },
    [referenceElement, setState]
  );

  useOnClickOutside(popperRef, handleOutsideClick);

  useLayoutEffect(() => {
    update && update();
  }, [update]);

  const hasDropdown = options.length > 0;
  const popperStyle = styles.popper as CSSObject | undefined;
  const isPopperReady = popperStyle != null && popperStyle.transform != null;

  return (
    <>
      <span ref={setReferenceElement}>
        <PicklistElement
          {...attributes}
          data-editor-element={props['data-editor-element']}
          handleClick={() => setState((state) => ({ ...state, isOpen: true }))}
          selected={selected}
          element={element}
          leftDelimiter={props.leftDelimiter}
          rightDelimiter={props.rightDelimiter}
        >
          {children}
        </PicklistElement>
      </span>

      {showPicklistOptionsInEditor && hasDropdown && isOpen && (
        <Portal>
          <ul
            role="listbox"
            data-testid="picklist-selector"
            css={css`
              z-index: 1;
              padding: 0;
              margin: 0;
              list-style: none;
              flex: 1;
              min-height: 0;
              background-color: ${Colors.gray2};
              border-radius: ${rootTheme.radius.xs}px;
              box-shadow: 2px 2px 4px 0 ${transparentize(Colors.gray1, 0.5)};
              overflow: auto;
              min-width: 200px;
              max-width: 500px;
              border: 1px solid ${transparentize(Colors.gray4, 0.5)};
              z-index: 10000;
              opacity: ${isPopperReady ? 1 : 0};
              pointer-events: ${isPopperReady ? 'auto' : 'none'};
            `}
            // Popper injects transform/top/left dynamically via inline styles; keep visual styling in the CSS block above.
            style={popperStyle}
            ref={handlePopperRef}
            onMouseLeave={() => setHoveredPicklistOptionIndex(null)}
          >
            <li
              css={`
                display: grid;
                grid-template-columns: 20px 1fr;
                grid-column-gap: 5px;
                padding: 5px 15px;
                font-weight: 500;
                color: ${Colors.gray8};
              `}
            >
              <Cell>#</Cell>
              <Cell>Name</Cell>
            </li>
            {options.map((item, index) => {
              const isHovered =
                hoveredPicklistOptionIndex != null &&
                hoveredPicklistOptionIndex < options.length &&
                index === hoveredPicklistOptionIndex;
              const isItemSelected =
                hoveredPicklistOptionIndex == null &&
                selectedPicklistOptionIds[element.picklistID] != null &&
                selectedPicklistOptionIds[element.picklistID] === item.id;

              return (
                <PicklistOption
                  id={item.id}
                  key={item.id}
                  onSelect={() => handleSelectPicklistItem(index)}
                  onMouseOver={() => setHoveredPicklistOptionIndex(index)}
                  isSelected={isHovered || isItemSelected}
                >
                  <Cell>{index + 1}</Cell>
                  <Cell>{getPicklistOptionDisplayName(item)}</Cell>
                </PicklistOption>
              );
            })}
          </ul>
        </Portal>
      )}
    </>
  );
};
